// A pending time proposal — the shadow row a participant's drag leaves at the
// new time (pages/index.js: `isProposal`, `originalEventId`, `proposedBy`).
// Accept moves the ORIGINAL event to the proposed time and deletes the
// shadow; Decline deletes the shadow and leaves the original where it is.
//
// Both ask first through the house confirm (CRUD + confirmation standard,
// rules 3–5): Accept moves an event for everyone invited, Decline deletes the
// proposal. Nothing is written before the person confirms, and a write that
// fails puts the rows back (saveEvent / deleteEvent raise their own error
// toast). Other calendar functions are reached through `this.call`, never by
// an import.

const _findOriginal = (root, proposal) =>
  (root.events || []).find((item) => item.id === proposal.originalEventId) || null

const _sameEmail = (a, b) => {
  const x = String(a || '')
    .trim()
    .toLowerCase()
  return (
    !!x &&
    x ===
      String(b || '')
        .trim()
        .toLowerCase()
  )
}

// Accept: whoever may write the ORIGINAL event (calendarCanWriteEvent — its
// organizer or a platform superadmin). With no original loaded there is
// nothing to move, so nobody gets Accept.
export function calendarCanAcceptProposal(proposal, root) {
  const r = root || (this && this.state && this.state.root) || null
  if (!r || !proposal || !proposal.isProposal || typeof this?.call !== 'function') return false
  const original = _findOriginal(r, proposal)
  return !!original && this.call('calendarCanWriteEvent', original, r) === true
}

// Decline: whoever may write the original, or the person who proposed the
// time (they withdraw their own proposal).
export function calendarCanDeclineProposal(proposal, root) {
  const r = root || (this && this.state && this.state.root) || null
  if (!r || !proposal || !proposal.isProposal || typeof this?.call !== 'function') return false
  if (_sameEmail(proposal.proposedBy, r.currentUserEmail)) return true
  const original = _findOriginal(r, proposal) || proposal
  return this.call('calendarCanWriteEvent', original, r) === true
}

// The event's own title for the question, or the generic "event".
const _titleOf = (el, root, proposal) => {
  const original = _findOriginal(root, proposal)
  return (
    (original && original.title) ||
    proposal.title ||
    el.call('polyglot', 'calendar.delete.eventFallback')
  )
}

// Put the original row back as it was, and the proposal next to it.
const _restoreProposal = (root, original, shadow) => {
  const next = (root.events || []).map((item) =>
    original && item.id === original.id ? original : item
  )
  if (shadow && !next.some((item) => item.id === shadow.id)) next.push(shadow)
  root.update({ events: next })
}

// The answer is a promise of the write's result: false on Cancel or on a
// failed write.
export function acceptEventProposal(proposal) {
  const root = this && this.state && this.state.root
  if (!root || !proposal || !proposal.isProposal) return Promise.resolve(false)
  const t = (key) => this.call('polyglot', key)
  const title = _titleOf(this, root, proposal)
  return new Promise((resolve) => {
    this.call('confirmDialog', {
      title: t('calendar.proposal.acceptTitle'),
      // A function replacement: the title is the person's own text.
      message: t('calendar.proposal.acceptMessage').replace('{title}', () => title),
      confirmText: t('calendar.detail.accept'),
      cancelText: t('common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => resolve(_acceptNow.call(this, root, proposal)),
      onCancel: () => resolve(false)
    })
  })
}

const _acceptNow = async function (root, proposal) {
  // Live rows, read at confirm time: a sync may have landed meanwhile.
  const live = root.events || []
  const original = _findOriginal(root, proposal)
  const shadow = live.find((item) => item.id === proposal.id) || proposal
  if (!original) return false
  const moved = {
    ...original,
    date: shadow.date,
    startTime: shadow.startTime,
    endTime: shadow.endTime
  }
  root.update({
    events: live
      .filter((item) => item.id !== shadow.id)
      .map((item) => (item.id === original.id ? moved : item))
  })
  this.call('closeEventDetailModal')
  // The move first: when it fails the proposal must stay, so it can be
  // accepted again.
  const saved = await this.call('saveEvent', moved, { falseOnFail: true })
  if (saved === false) {
    _restoreProposal(root, original, shadow)
    return false
  }
  const deleted = await this.call('deleteEvent', shadow.id)
  if (deleted === false) {
    _restoreProposal(root, null, shadow)
    return false
  }
  return true
}

export function declineEventProposal(proposal) {
  const root = this && this.state && this.state.root
  if (!root || !proposal || !proposal.isProposal) return Promise.resolve(false)
  const t = (key) => this.call('polyglot', key)
  const title = _titleOf(this, root, proposal)
  return new Promise((resolve) => {
    this.call('confirmDialog', {
      title: t('calendar.proposal.declineTitle'),
      message: t('calendar.proposal.declineMessage').replace('{title}', () => title),
      confirmText: t('calendar.detail.decline'),
      cancelText: t('common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => resolve(_declineNow.call(this, root, proposal)),
      onCancel: () => resolve(false)
    })
  })
}

const _declineNow = async function (root, proposal) {
  const live = root.events || []
  const shadow = live.find((item) => item.id === proposal.id) || proposal
  root.update({ events: live.filter((item) => item.id !== shadow.id) })
  this.call('closeEventDetailModal')
  const deleted = await this.call('deleteEvent', shadow.id)
  if (deleted === false) {
    _restoreProposal(root, null, shadow)
    return false
  }
  return true
}
